import { useState, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { AlertCircle, FolderTree } from "lucide-react";
import { useDocumentFolderTemplates } from "@/hooks/use-document-folder-templates";
import { useFolderTree } from "@/hooks/use-document-folders";
import { useFolders } from "@/hooks/use-folders";
import { useAuth } from "@/hooks/use-auth";
import { CreateFolderDialog } from "./CreateFolderDialog";
import { EditFolderDialog } from "./EditFolderDialog";
import { DeleteFolderDialog } from "./DeleteFolderDialog";
import type { TemplateTreeContextValue } from "./TemplateTreeContext";
import { ClaimFoldersSection } from "./ClaimFoldersSection";
import { DocumentTemplatesSection } from "./DocumentTemplatesSection";

export function DocumentSettings() {
  const { user } = useAuth();
  const {
    folders: claimFolders,
    isLoading: isClaimFoldersLoading,
    error: claimFoldersError,
    refetch: refetchClaimFolders,
    isAdmin: canManageAllClaimFolders,
    currentUserId,
    createDialogOpen,
    setCreateDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    editingFolder,
    setEditingFolder,
    folderToDelete,
    setFolderToDelete,
    createFolderMutation,
    updateFolderMutation,
    deleteFolderMutation,
  } = useFolders();
  const {
    templates,
    isLoading,
    error: templatesError,
    refetch: refetchTemplates,
    createTemplate,
    isCreating,
    updateTemplate,
    deleteTemplate,
  } = useDocumentFolderTemplates();

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const tree = useFolderTree();

  // ── Delete confirmation dialog state ──
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate(deleteTarget.id);
    } catch {
      // Error toast shown by hook's onError callback
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteTemplate]);

  const handleDelete = useCallback((id: number, name: string) => {
    setDeleteTarget({ id, name });
  }, []);

  const handleCreate = useCallback(async (parentId: number | null) => {
    const name = tree.newName.trim();
    if (!name) return;
    try {
      await createTemplate({ name, parentId });
      if (parentId !== null) tree.expandNode(parentId);
    } catch {
      // Error toast shown by hook's onError callback
    } finally {
      tree.cancelCreate();
    }
  }, [tree, createTemplate]);

  const handleRename = useCallback(async () => {
    if (!tree.renamingId || !tree.renameValue.trim()) {
      tree.cancelRename();
      return;
    }
    try {
      await updateTemplate({ templateId: tree.renamingId, name: tree.renameValue.trim() });
    } catch {
      // Error toast shown by hook's onError callback
    } finally {
      tree.cancelRename();
    }
  }, [tree, updateTemplate]);

  // ── Pre-computed lookup map (avoids O(n) filter + sort in every TemplateNode render) ──
  const childTemplatesByParent = useMemo(() => {
    const map = new Map<number | null, typeof templates>();
    for (const t of templates) {
      const key = t.parentId ?? null;
      const list = map.get(key);
      if (list) list.push(t);
      else map.set(key, [t]);
    }
    // Sort each sibling list once
    for (const list of map.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return map;
  }, [templates]);

  // ── Context value ──
  const ctxValue = useMemo<TemplateTreeContextValue>(() => ({
    allTemplates: templates,
    childTemplatesByParent,
    expandedIds: tree.expandedIds,
    toggleExpanded: tree.toggleExpanded,
    renamingId: tree.renamingId,
    renameValue: tree.renameValue,
    setRenameValue: tree.setRenameValue,
    onRenameSubmit: handleRename,
    onRenameCancel: tree.cancelRename,
    onStartRename: tree.startRename,
    creatingInParent: tree.creatingInParent,
    newName: tree.newName,
    setNewName: tree.setNewName,
    onCreateSubmit: handleCreate,
    onCreateCancel: tree.cancelCreate,
    onStartCreate: tree.startCreate,
    onDelete: handleDelete,
    isCreating,
    isAdmin,
  }), [
    templates, childTemplatesByParent, isCreating, isAdmin,
    tree.expandedIds, tree.renamingId, tree.renameValue,
    tree.creatingInParent, tree.newName,
    tree.toggleExpanded, tree.setRenameValue, tree.cancelRename,
    tree.startRename, tree.setNewName, tree.cancelCreate, tree.startCreate,
    handleRename, handleCreate, handleDelete,
  ]);

  if (!user?.companyId) {
    return (
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <FolderTree className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Company Found</h3>
            <p className="text-muted-foreground">
              You need to be associated with a company to manage document folder templates.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const rootTemplates = childTemplatesByParent.get(null) ?? [];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {!isAdmin && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>View Only</AlertTitle>
          <AlertDescription>
            You are viewing the document folder templates. Only administrators and owners can make changes.
          </AlertDescription>
        </Alert>
      )}

      <ClaimFoldersSection
        folders={claimFolders}
        isLoading={isClaimFoldersLoading}
        error={claimFoldersError}
        canManageAllClaimFolders={canManageAllClaimFolders}
        currentUserId={currentUserId}
        isCreating={createFolderMutation.isPending}
        onRetry={() => void refetchClaimFolders()}
        setCreateDialogOpen={setCreateDialogOpen}
        setEditingFolder={setEditingFolder}
        setEditDialogOpen={setEditDialogOpen}
        setFolderToDelete={setFolderToDelete}
        setDeleteDialogOpen={setDeleteDialogOpen}
      />

      <DocumentTemplatesSection
        templates={templates}
        isLoading={isLoading}
        error={templatesError}
        isAdmin={isAdmin}
        isCreating={isCreating}
        rootTemplates={rootTemplates}
        ctxValue={ctxValue}
        onCreateRoot={() => tree.startCreate("root")}
        onRetry={() => void refetchTemplates()}
        handleCreate={handleCreate}
      />

      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        onConfirm={handleDeleteConfirm}
        title="Delete Template"
        description={
          deleteTarget
            ? `Delete "${deleteTarget.name}" template? Child templates will also be deleted.`
            : ""
        }
      />

      <CreateFolderDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onConfirm={async (data) => {
          try {
            await createFolderMutation.mutateAsync(data);
          } catch {
            // Error toast shown by the mutation's onError callback.
          }
        }}
        isCreating={createFolderMutation.isPending}
      />

      <EditFolderDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        folder={editingFolder}
        onSave={async (data) => {
          if (!editingFolder) return;
          try {
            await updateFolderMutation.mutateAsync({ id: editingFolder.id, data });
          } catch {
            // Error toast shown by the mutation's onError callback.
          }
        }}
        isLoading={updateFolderMutation.isPending}
      />

      <DeleteFolderDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        folder={folderToDelete}
        onConfirm={() => {
          if (!folderToDelete) return;
          deleteFolderMutation.mutate(folderToDelete.id);
        }}
        isDeleting={deleteFolderMutation.isPending}
      />
    </div>
  );
}